// src/layout/Layout.js
import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from "@react-keycloak/web";

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';

import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import AnalyticsIcon from '@mui/icons-material/Analytics'; // Example Analytics Icon
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';

import { Main, DrawerHeader, Footer } from '../components/LayoutComponents';
import FooterContent from '../components/FooterContent';
import { getLocalStorageItem, setLocalStorageItem, clearApplicationStorage } from '../utils/localStorage';

const drawerWidth = 276;

export default function Layout() {
    const { keycloak } = useKeycloak();
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedOption, setSelectedOption] = useState(() => {
        return getLocalStorageItem('selectedOption') || null;
    });
    const [selectedCountry, setSelectedCountry] = useState(() => {
        return getLocalStorageItem('selectedCountry') || null;
    });

    // State to track if dashboard has been accessed through proper flow
    const [dashboardAccessed, setDashboardAccessed] = useState(() => {
        return getLocalStorageItem('dashboardAccessed') === 'true';
    });

    // State to manage open/close for nested items
    const [openSubmenu, setOpenSubmenu] = useState(null);

    // Determine if the current route is Homepage
    const isHomepage = location.pathname === '/' || location.pathname === '/alt';

    // Initialize drawer state: closed if on homepage, open otherwise
    const [open, setOpen] = useState(!isHomepage);

    const handleDrawerOpen = () => setOpen(true);
    const handleDrawerClose = () => setOpen(false);

    useEffect(() => {
        // Update drawer state based on current route
        setOpen(!isHomepage);

        if (location.state && location.state.selectedOption) {
            setSelectedOption(location.state.selectedOption);
            setLocalStorageItem('selectedOption', location.state.selectedOption);
        }
        if (location.state && location.state.selectedCountry) {
            setSelectedCountry(location.state.selectedCountry);
            setLocalStorageItem('selectedCountry', location.state.selectedCountry);
        }
    }, [location, isHomepage]);

    // Add useEffect to listen for localStorage changes and update selectedOption
    useEffect(() => {
        const handleStorageChange = (event) => {
            if (event.key === 'selectedOption') {
                setSelectedOption(event.newValue);
            }
            if (event.key === 'selectedCountry') {
                setSelectedCountry(event.newValue);
            }
            if (event.key === 'dashboardAccessed') {
                setDashboardAccessed(event.newValue === 'true');
            }
        };

        const handleCustomStorageChange = (event) => {
            const { key, newValue } = event.detail;
            if (key === 'selectedOption') {
                setSelectedOption(newValue);
            }
            if (key === 'selectedCountry') {
                setSelectedCountry(newValue);
            }
            if (key === 'dashboardAccessed') {
                setDashboardAccessed(newValue === 'true');
            }
        };

        // Listen for storage events (when localStorage changes in other tabs/windows)
        window.addEventListener('storage', handleStorageChange);

        // Listen for custom storage events (when localStorage changes in same tab)
        window.addEventListener('customStorageChange', handleCustomStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('customStorageChange', handleCustomStorageChange);
        };
    }, []);

    const handleSubmenuClick = (menuItem) => {
        if (openSubmenu === menuItem.title) {
            setOpenSubmenu(null);
        } else {
            setOpenSubmenu(menuItem.title);
        }
    };

    const navItems = [
        {
            title: 'Homepage',
            icon: <HomeIcon sx={{ color: theme.palette.primary.main }} />,
            path: '/'
        },
        // {
        //     title: 'Select Country old',
        //     icon: <TravelExploreIcon sx={{ color: theme.palette.primary.main }} />,
        //     path: '/map4'
        // },
        {
            title: 'Select Country',
            icon: <TravelExploreIcon sx={{ color: theme.palette.primary.main }} />,
            path: '/map'
        },
        ...(selectedCountry ? [{
            title: 'Data Insertion',
            icon: <InsertDriveFileIcon sx={{ color: theme.palette.primary.main }} />,
            path: '/dataInsert'
        }] : []),
        // ...(selectedOption === 'Aggregated' ? [{
        //     title: 'Flexibility Model',
        //     icon: <ModelTrainingIcon sx={{ color: theme.palette.primary.main }} />,
        //     path: '/flexibility'
        // }] : []),
        // ...(selectedOption === 'Disaggregated' ? [{
        //     title: 'Disaggregation',
        //     icon: <CallSplitIcon sx={{ color: theme.palette.primary.main }} />,
        //     path: '/disaggregation'
        // }] : []),
        ...(dashboardAccessed ? [{
            title: 'Dashboard',
            icon: <DashboardIcon sx={{ color: theme.palette.primary.main }} />,
            path: '/dashboard',
            children: [
                {
                    title: 'Analytics',
                    icon: <AnalyticsIcon sx={{ color: theme.palette.primary.main }} />,
                    path: `/dashboard/${(selectedOption || 'aggregated').toLowerCase()}/analytics`
                },
                {
                    title: 'Forecasts',
                    icon: <BarChartIcon sx={{ color: theme.palette.primary.main }} />,
                    path: `/dashboard/${(selectedOption || 'aggregated').toLowerCase()}/forecasts`
                }
            ]
        }] : []),
        // Add more items as needed
    ];

    const handleSignOut = () => {
        try {
            // Clear application-specific localStorage before logout
            clearApplicationStorage();
            
            // Small delay to ensure cleanup completes before logout redirect
            setTimeout(() => {
                keycloak.logout();
            }, 100);
        } catch (error) {
            console.error('Error during logout cleanup:', error);
            // Still proceed with logout even if cleanup fails
            keycloak.logout();
        }
    };

    return (
        <>
            <Box sx={{ display: 'flex', minHeight: `calc(100vh - 60px)` }}>
                <CssBaseline />
                {!open && (
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{
                            position: 'fixed',
                            top: 10,
                            left: 10,
                            color: 'black',
                            zIndex: 1300
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}

                <Drawer
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                        },
                    }}
                    variant="persistent"
                    anchor="left"
                    open={open}
                >
                    <DrawerHeader sx={{ display: 'flex', alignItems: 'center', padding: theme.spacing(1) }}>
                        <img
                            src={`/images/dedalus_logo.png`}
                            alt="DEDALUS logo"
                            height={'50px'} // Adjust the height as necessary
                            style={{ objectFit: 'cover', marginRight: theme.spacing(2) }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography fontWeight="bold" variant="body1" sx={{ color: 'primary.main' }}>
                                DEDALUS
                            </Typography>
                            <Typography variant="body2" fontSize={12}>
                                Flexibility Assessment Tool
                            </Typography>
                        </Box>
                        <IconButton onClick={handleDrawerClose} sx={{ marginLeft: 'auto' }}>
                            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    </DrawerHeader>

                    <Divider />
                    <List>
                        {navItems.map((menuItem) => (
                            menuItem.children ? (
                                <React.Fragment key={menuItem.title}>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => handleSubmenuClick(menuItem)}
                                            sx={{
                                                background: '', // No background for parent
                                                border: 'none',
                                                borderRadius: '10px',
                                                margin: 1,
                                                width: '95%',
                                                justifyContent: 'space-between',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.action.hover,
                                                },
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <ListItemIcon>
                                                    {menuItem.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography fontWeight={500} fontSize={17} align={'left'}>
                                                            {menuItem.title}
                                                        </Typography>
                                                    }
                                                />
                                            </Box>
                                            {openSubmenu === menuItem.title ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </ListItemButton>
                                    </ListItem>
                                    <Collapse in={openSubmenu === menuItem.title} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            {menuItem.children.map((child) => {
                                                const isActive = location.pathname === child.path ||
                                                    (location.pathname.includes('/dashboard/') && child.path.includes('/dashboard/') &&
                                                        location.pathname.includes(child.path.split('/').pop()));
                                                return (
                                                    <ListItem key={child.title} disablePadding
                                                        sx={{
                                                            pl: 4, // Indentation
                                                            background: isActive
                                                                ? theme.palette.action.selected
                                                                : '',
                                                            borderLeft: isActive
                                                                ? `4px solid ${theme.palette.primary.main}`
                                                                : '4px solid transparent',
                                                            borderRadius: '10px',
                                                            margin: 1,
                                                            width: '90%',
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.action.hover,
                                                            },
                                                        }}>
                                                        <ListItemButton onClick={() => navigate(child.path)}>
                                                            <ListItemIcon>
                                                                {child.icon}
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    <Typography fontWeight={500} fontSize={17} align={'left'}>
                                                                        {child.title}
                                                                    </Typography>
                                                                }
                                                                sx={{
                                                                    color: isActive
                                                                        ? theme.palette.text.primary
                                                                        : 'inherit',
                                                                }}
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                )
                                            })}
                                        </List>
                                    </Collapse>
                                </React.Fragment>
                            ) : (
                                <ListItem key={menuItem.title} disablePadding
                                    sx={{
                                        background: location.pathname === menuItem.path
                                            ? theme.palette.action.selected
                                            : '',
                                        borderLeft: location.pathname === menuItem.path
                                            ? `4px solid ${theme.palette.primary.main}`
                                            : '4px solid transparent',
                                        borderRadius: '10px',
                                        margin: 1,
                                        width: '95%',
                                        '&:hover': {
                                            backgroundColor: theme.palette.action.hover,
                                        },
                                    }}>
                                    <ListItemButton onClick={menuItem.path ? () => navigate(menuItem.path) : menuItem.onClick}>
                                        <ListItemIcon>{menuItem.icon}</ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography fontWeight={500} fontSize={17} align={'left'}>
                                                    {menuItem.title}
                                                </Typography>
                                            }
                                            sx={{
                                                color: location.pathname === menuItem.path
                                                    ? theme.palette.text.primary
                                                    : 'inherit',
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            )
                        ))}
                    </List>
                    <Divider />
                    <List>
                        <ListItem key="my-profile" disablePadding
                            sx={{
                                background: location.pathname === '/profile'
                                    ? theme.palette.action.selected
                                    : '',
                                borderLeft: location.pathname === '/profile'
                                    ? `4px solid ${theme.palette.primary.main}`
                                    : '4px solid transparent',
                                borderRadius: '10px',
                                margin: 1,
                                width: '95%',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}>
                            <ListItemButton onClick={() => navigate('/profile')}>
                                <ListItemIcon><AccountCircleIcon sx={{ color: theme.palette.primary.main }} /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography fontWeight={500} fontSize={17} align={'left'}>
                                            My Profile
                                        </Typography>
                                    }
                                    sx={{
                                        color: location.pathname === '/profile'
                                            ? theme.palette.text.primary
                                            : 'inherit',
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                        <ListItem key="logout" disablePadding
                            sx={{
                                borderRadius: '10px',
                                margin: 1,
                                width: '95%',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}>
                            <ListItemButton onClick={handleSignOut}>
                                <ListItemIcon><LogoutIcon sx={{ color: theme.palette.primary.main }} /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography fontWeight={500} fontSize={17} align={'left'}>
                                            Logout
                                        </Typography>
                                    }
                                />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Drawer>

                <Main open={open}>
                    <Outlet />
                </Main>
            </Box>

            <Footer open={open}>
                <FooterContent />
            </Footer>
        </>
    );
}
